import { ref, nextTick, Ref, computed } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import dayjs from "dayjs";
import { formatDate } from "@/helpers/date";
import {
  formatPrice,
  formatPriceToNumber,
} from "@/helpers/transformation/price";
import { ExtendedLot } from "./useLotsData";
import { LotStatus } from "@/enums/LotStatus";
import { PriceType } from "@/v2/enums/PriceType";
import { LotInterface } from "@/types/LotInterface";
import { InitialHoldInterface } from "@/types/InitialHoldInterface";
import { CustomColumnDto, CustomColumnTypeEnum } from "./useLotsColumns";
import LotConditionsEnum from "@/v2/enums/LotConditions";

interface FieldConfig {
  editable: boolean;
  type: "text" | "date" | "dropdown" | "number";
  property?: string;
  options?: string[];
  getValue?: (lot: ExtendedLot) => string;
  getDisplayValue?: (lot: ExtendedLot) => string;
  processValue?: (value: string) => string | number | null;
  validate?: (value: string) => boolean;
}

export function useLotsEditing(
  lots: Ref<ExtendedLot[]>,
  initialHolds: Ref<InitialHoldInterface[] | null>,
  customColumns: Ref<CustomColumnDto[]>
) {
  const store = useStore();
  const toast = useToast();

  const editingLot = ref<string | null>(null);
  const editingField = ref<string | null>(null);
  const editingValue = ref<string>("");
  const activeLoaders = ref(0);

  const lotStatusOptions = [
    LotStatus.NOT_RELEASED,
    LotStatus.AVAILABLE,
    LotStatus.HOLD,
    LotStatus.SETTLED,
    LotStatus.UNDER_CONTRACT,
    LotStatus.UNCONDITIONAL,
  ];

  const lotConditionsOptions = Object.values(LotConditionsEnum);

  const getLotBuyers = (lot: ExtendedLot): string => {
    const lotBuyers = lot.lotBuyers;

    if (!lotBuyers) return "--";

    const buyers = [];

    if (lotBuyers.firstBuyer) {
      buyers.push(lotBuyers.firstBuyer);
    }

    if (lotBuyers.secondBuyer) {
      buyers.push(lotBuyers.secondBuyer);
    }

    return buyers.length > 0 ? buyers.join(", ") : "--";
  };
  const fieldsConfig: Record<string, FieldConfig> = {
    lotBuyers: {
      editable: true,
      type: "text",
      property: "lotBuyers",
      getValue: (lot) => getLotBuyers(lot),
      getDisplayValue: (lot) => getLotBuyers(lot),
      processValue: (value) => {
        try {
          return JSON.parse(value);
        } catch (error) {
          console.error("Invalid JSON for lotBuyers", value, error);
          toast.error("An error occurred while processing the lot buyers.");
          return null;
        }
      },
    },
    registrationDate: {
      editable: true,
      type: "date",
      property: "registrationDate",
      getValue: (lot) =>
        lot.registrationDate
          ? formatDate(lot.registrationDate, "YYYY-MM-DD")
          : "",
      getDisplayValue: (lot) =>
        lot.registrationDate
          ? formatDate(lot.registrationDate, "D MMM YYYY")
          : "--",
      processValue: (value) => {
        if (value && dayjs(value).isValid()) {
          return dayjs(value).toISOString();
        }

        return null;
      },
    },
    exchanged: {
      editable: true,
      type: "date",
      property: "exchangedDate",
      getValue: (lot) =>
        lot.exchangedDate ? formatDate(lot.exchangedDate, "YYYY-MM-DD") : "",
      getDisplayValue: (lot) =>
        lot.exchangedDate ? formatDate(lot.exchangedDate, "D MMM YYYY") : "--",
      processValue: (value) => {
        if (value && dayjs(value).isValid()) {
          return dayjs(value).toISOString();
        }

        return null;
      },
    },
    financeDue: {
      editable: true,
      type: "date",
      property: "financeDueDate",
      getValue: (lot) =>
        lot.financeDueDate ? formatDate(lot.financeDueDate, "YYYY-MM-DD") : "",
      getDisplayValue: (lot) =>
        lot.financeDueDate
          ? formatDate(lot.financeDueDate, "D MMM YYYY")
          : "--",
      processValue: (value) => {
        if (value && dayjs(value).isValid()) {
          return dayjs(value).toISOString();
        }

        return null;
      },
    },
    settlement: {
      editable: true,
      type: "date",
      property: "settlementDate",
      getValue: (lot) =>
        lot.settlementDate ? formatDate(lot.settlementDate, "YYYY-MM-DD") : "",
      getDisplayValue: (lot) =>
        lot.settlementDate
          ? formatDate(lot.settlementDate, "D MMM YYYY")
          : "--",
      processValue: (value) => {
        if (value && dayjs(value).isValid()) {
          return dayjs(value).toISOString();
        }

        return null;
      },
    },
    status: {
      editable: true,
      type: "dropdown",
      property: "status",
      options: lotStatusOptions,
      getValue: (lot) => lot.status,
      getDisplayValue: (lot) =>
        lot.status === LotStatus.EOI ? "Pre-Contract" : lot.status,
      processValue: (value) => value,
      validate: (value) => lotStatusOptions.includes(value as LotStatus),
    },
    price: {
      editable: true,
      type: "number",
      property: "priceTracking",
      getValue: (lot) => {
        const lotPrice = lot.priceTracking;

        if (!lotPrice) return "";

        return lotPrice.value?.toString() || "";
      },
      getDisplayValue: (lot) => {
        const lotPrice = lot.priceTracking;

        if (!lotPrice) return "--";

        if (lotPrice.type === PriceType.HIDE) return "Hide";

        if (lotPrice.type === PriceType.OFFERS) return "Open to Offers";

        if (!lotPrice.value) return "--";

        const formattedPrice = formatPrice(lotPrice.value);

        return lotPrice.type === PriceType.FROM
          ? `From ${formattedPrice}`
          : formattedPrice;
      },
      processValue: (value) => {
        if (!value) return null;

        const numValue = formatPriceToNumber(value);

        return numValue > 0 ? numValue : null;
      },
      validate: (value) => {
        if (!value) return true;

        const numValue = formatPriceToNumber(value);

        return !isNaN(numValue) && numValue >= 0;
      },
    },
    size: {
      editable: false,
      type: "text",
      property: "size",
      getValue: (lot) => (lot.size ? lot.size.toString() : ""),
      getDisplayValue: (lot) => (lot.size ? `${lot.size}` : "--"),
    },
    deposit: {
      editable: true,
      type: "number",
      property: "deposit",
      getValue: (lot) => (lot.deposit ? lot.deposit.toString() : ""),
      getDisplayValue: (lot) => {
        if (!lot.deposit) return "--";

        return formatPrice(lot.deposit);
      },
      processValue: (value) => {
        if (!value) return null;

        const numValue = formatPriceToNumber(value);

        return numValue > 0 ? numValue : null;
      },
      validate: (value) => {
        if (!value) return true;

        const numValue = formatPriceToNumber(value);

        return !isNaN(numValue) && numValue >= 0;
      },
    },
    conditions: {
      editable: true,
      type: "dropdown",
      property: "conditions",
      options: lotConditionsOptions,
      getValue: (lot) => lot.conditions || "",
      getDisplayValue: (lot) => lot.conditions || "--",
      processValue: (value) => value,
      validate: (value) =>
        lotConditionsOptions.includes(value as LotConditionsEnum),
    },
  };

  const generateCustomFieldConfigs = (): Record<string, FieldConfig> => {
    const configs: Record<string, FieldConfig> = {};

    customColumns.value.forEach((column) => {
      const key = `custom_${column.name}`;

      configs[key] = {
        editable: true,
        type: getCustomColumnInputType(column.type),
        property: `customColumns.${column.name}`,
        getValue: (lot) => getCustomColumnValue(lot, column.name),
        getDisplayValue: (lot) => getCustomColumnDisplayValue(lot, column),
        processValue: (value) => processCustomColumnValue(value, column.type),
        validate: (value) => validateCustomColumnValue(value, column.type),
      };
    });

    return configs;
  };

  const getCustomColumnInputType = (
    type: CustomColumnTypeEnum
  ): "text" | "date" | "number" => {
    switch (type) {
      case CustomColumnTypeEnum.TEXT:
        return "text";
      case CustomColumnTypeEnum.NUMBER:
      case CustomColumnTypeEnum.DOLLAR:
        return "number";
      case CustomColumnTypeEnum.DATE:
        return "date";
      default:
        return "text";
    }
  };

  const getCustomColumnValue = (
    lot: ExtendedLot,
    columnName: string
  ): string => {
    const customData = (
      lot as ExtendedLot & {
        customColumns?: Record<string, string | number | Date>;
      }
    ).customColumns;

    if (!customData || !customData[columnName]) return "";

    const value = customData[columnName];

    if (value instanceof Date) {
      return formatDate(value.toISOString(), "YYYY-MM-DD");
    }

    return String(value || "");
  };

  const getCustomColumnDisplayValue = (
    lot: ExtendedLot,
    column: CustomColumnDto
  ): string => {
    const customData = (
      lot as ExtendedLot & {
        customColumns?: Record<string, string | number | Date>;
      }
    ).customColumns;

    if (!customData || !customData[column.name]) return "--";

    const value = customData[column.name];

    switch (column.type) {
      case CustomColumnTypeEnum.DATE:
        if (value instanceof Date) {
          return formatDate(value.toISOString(), "D MMM YYYY");
        } else if (typeof value === "string" && value) {
          return formatDate(value, "D MMM YYYY");
        }

        return "--";
      case CustomColumnTypeEnum.DOLLAR:
        return value ? formatPrice(Number(value)) : "--";
      case CustomColumnTypeEnum.NUMBER:
        return value ? String(value) : "--";
      case CustomColumnTypeEnum.TEXT:
      default:
        return value ? String(value) : "--";
    }
  };

  const processCustomColumnValue = (
    value: string,
    type: CustomColumnTypeEnum
  ): string | number | null => {
    if (!value) return null;

    switch (type) {
      case CustomColumnTypeEnum.DATE: {
        if (dayjs(value).isValid()) {
          return dayjs(value).toISOString();
        }

        return null;
      }
      case CustomColumnTypeEnum.NUMBER: {
        const numValue = parseFloat(value);

        return isNaN(numValue) ? null : numValue;
      }
      case CustomColumnTypeEnum.DOLLAR: {
        const dollarValue = formatPriceToNumber(value);

        return isNaN(dollarValue) ? null : dollarValue;
      }
      case CustomColumnTypeEnum.TEXT:
      default:
        return value;
    }
  };

  const validateCustomColumnValue = (
    value: string,
    type: CustomColumnTypeEnum
  ): boolean => {
    if (!value) return true;

    switch (type) {
      case CustomColumnTypeEnum.DATE:
        return dayjs(value).isValid();
      case CustomColumnTypeEnum.NUMBER:
        return !isNaN(parseFloat(value));
      case CustomColumnTypeEnum.DOLLAR: {
        const dollarValue = formatPriceToNumber(value);

        return !isNaN(dollarValue) && dollarValue >= 0;
      }
      case CustomColumnTypeEnum.TEXT:
      default:
        return true;
    }
  };

  const allFieldsConfig = computed(() => ({
    ...fieldsConfig,
    ...generateCustomFieldConfigs(),
  }));

  const columnPropertyMap = computed(() =>
    Object.entries(allFieldsConfig.value).reduce((acc, [key, config]) => {
      if (config.property) {
        acc[key] = config.property;
      }

      return acc;
    }, {} as Record<string, string>)
  );

  const columnInputTypeMap = computed(() =>
    Object.entries(allFieldsConfig.value).reduce((acc, [key, config]) => {
      acc[key] = config.type;
      return acc;
    }, {} as Record<string, string>)
  );

  const getFieldType = (fieldKey: string): string => {
    return columnInputTypeMap.value[fieldKey] || "text";
  };

  const getColumnValue = (lot: ExtendedLot, columnKey: string): string => {
    const config = allFieldsConfig.value[columnKey];

    if (config?.getValue) {
      return config.getValue(lot);
    }

    const fallbackValue = (lot as Record<string, unknown>)[columnKey];

    return fallbackValue != null ? String(fallbackValue) : "";
  };

  const getColumnDisplayValue = (
    lot: ExtendedLot,
    columnKey: string
  ): string => {
    const config = allFieldsConfig.value[columnKey];

    if (config?.getDisplayValue) {
      return config.getDisplayValue(lot);
    }

    const fallbackValue = (lot as Record<string, unknown>)[columnKey];

    return fallbackValue != null ? String(fallbackValue) : "--";
  };

  const isFieldEditable = (fieldKey: string): boolean => {
    const config = allFieldsConfig.value[fieldKey];

    if (config) {
      return config.editable;
    }

    return false;
  };

  const getFieldOptions = (fieldKey: string): string[] => {
    return allFieldsConfig.value[fieldKey]?.options || [];
  };

  const startEditing = (
    lotId: string,
    columnKey: string,
    currentValue: string
  ) => {
    const config = allFieldsConfig.value[columnKey];

    if (config && !config.editable) {
      toast.info("This field cannot be edited");
      return;
    }

    editingLot.value = lotId;
    editingField.value = columnKey;
    editingValue.value = currentValue;

    nextTick(() => {
      const input = document.querySelector(
        'input[ref="editInput"]'
      ) as HTMLInputElement;

      if (input) {
        input.focus();
        input.select();
      }
    });
  };

  const saveEdit = async () => {
    if (!editingLot.value || !editingField.value) return;

    const lot = lots.value.find((l) => l._id === editingLot.value);

    if (!lot) return;

    const columnKey = editingField.value;
    const config = allFieldsConfig.value[columnKey];
    const currentValue = editingValue.value;

    if (config && !config.editable) {
      toast.info(`${columnKey} field cannot be edited`);
      cancelEdit();
      return;
    }

    if (config?.validate && !config.validate(currentValue)) {
      toast.error(`Invalid ${columnKey} value`);
      cancelEdit();
      return;
    }

    let processedValue: string | number | null = currentValue;

    if (config?.processValue) {
      processedValue = config.processValue(currentValue);
    }

    const propertyToUpdate = columnPropertyMap.value[columnKey] || columnKey;

    activeLoaders.value++;

    try {
      const updateData = {
        [propertyToUpdate]: processedValue,
      };

      if (
        columnKey === "status" &&
        processedValue === LotStatus.UNCONDITIONAL
      ) {
        updateData["conditions"] = LotConditionsEnum.UNCONDITIONAL;
      }

      await store.dispatch("lot/updateLotV2", {
        id: editingLot.value,
        data: updateData,
      });

      if (columnKey.startsWith("custom_")) {
        const customColumnName = columnKey.replace("custom_", "");
        const lotWithCustom = lot as ExtendedLot & {
          customColumns?: Record<string, string | number | Date>;
        };

        if (!lotWithCustom.customColumns) {
          lotWithCustom.customColumns = {};
        }

        if (processedValue !== null) {
          lotWithCustom.customColumns[customColumnName] = processedValue;
        } else {
          delete lotWithCustom.customColumns[customColumnName];
        }
      } else {
        if (processedValue !== null) {
          (lot as Record<string, unknown>)[propertyToUpdate] = processedValue;
        } else {
          delete (lot as Record<string, unknown>)[propertyToUpdate];
        }
      }

      if (
        columnKey === "status" &&
        processedValue === LotStatus.UNCONDITIONAL
      ) {
        (lot as Record<string, unknown>)["conditions"] =
          LotConditionsEnum.UNCONDITIONAL;
      }

      toast.success("Lot updated successfully");
    } catch (error) {
      console.error("Error updating lot:", error);
      toast.error("Failed to update lot. Please try again.");
    } finally {
      activeLoaders.value--;
    }

    cancelEdit();
  };

  const cancelEdit = () => {
    editingLot.value = null;
    editingField.value = null;
    editingValue.value = "";
  };

  const updateEditingValue = (value: string) => {
    editingValue.value = value;
  };

  return {
    editingLot,
    editingField,
    editingValue,
    fieldsConfig: allFieldsConfig,
    columnPropertyMap,
    columnInputTypeMap,
    getFieldType,
    lotStatusOptions,
    getColumnValue,
    getColumnDisplayValue,
    isFieldEditable,
    getFieldOptions,
    startEditing,
    saveEdit,
    cancelEdit,
    updateEditingValue,
  };
}
